import { BaseThemeType, Button, Link, styled, useMediaQuery } from 'opensolar-ui'
import { FC } from 'react'
import { HighlightConfig } from '../../expoStand'
import Countdown from './Countdown'

interface PropTypes {
  highlight: HighlightConfig
  brandLogoUrl?: string
  isExpoHomepage?: boolean | undefined
  assets?: {
    promoImage?: string
  }
}

const CardWrapper = styled('div', { name: 'CardWrapper' })<{
  backgroundUrl: string
  backgroundColor?: string
  ui3styling: boolean | undefined
}>(({ backgroundUrl, backgroundColor, ui3styling, theme }) => {
  const getBackgroundColor = (): string => {
    if (backgroundUrl) return ''
    if (ui3styling) return ''
    if (backgroundColor) return backgroundColor
    return 'white'
  }

  return {
    flexGrow: 1,
    flexBasis: 400,
    borderRadius: 10,
    backgroundImage: backgroundUrl ? `url("${backgroundUrl}")` : '',
    backgroundColor: getBackgroundColor(),
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center left',
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: theme?.typography.fontFamily,
    boxShadow: ui3styling
      ? 'none'
      : `
      0 1px 2px rgba(0, 0, 0, 0.06),
      0 2px 4px rgba(0, 0, 0, 0.04),
      0 4px 8px rgba(0, 0, 0, 0.03)
    `,
    minHeight: 235,
    [theme.breakpoints.down('sm')]: {
      minHeight: 174.5,
    },
  }
})

const CardTitle = styled('h1', { name: 'CardTitle' })<{
  ui3styling?: boolean
  fullWidth?: boolean
}>(({ ui3styling, fullWidth, theme }) => ({
  fontSize: 32,
  fontWeight: 600,
  margin: '15px 0',
  maxWidth: '80%',
  color: 'inherit',
  ...(ui3styling && {
    fontSize: '3rem',
    background: 'linear-gradient(180deg, #1751D0 0%, #3C91E6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
    [theme.breakpoints.down('md')]: {
      fontSize: '2rem',
      maxWidth: '100%',
    },
  }),
  ...(!ui3styling && {
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
      maxWidth: '95%',
    },
  }),
}))

const CardDesc = styled('h3', { name: 'CardDesc' })<{
  ui3styling: boolean | undefined
}>(({ ui3styling, theme }) => ({
  fontSize: ui3styling ? 24 : 16,
  color: ui3styling ? '#4C4B53' : 'inherit',
  maxWidth: '65%',
  fontWeight: 400,
  lineHeight: ui3styling ? '40px' : '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: ui3styling ? '1rem' : 16,
    lineHeight: '130%',
    maxWidth: '100%',
  },
}))

const MainContent = styled('div', { name: 'MainContent' })<{
  enabledAlignTextToCenter: boolean | undefined
}>(({ enabledAlignTextToCenter, theme }) => ({
  padding: 32,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: enabledAlignTextToCenter ? 'center' : 'flex-start',
  textAlign: enabledAlignTextToCenter ? 'center' : 'left',
  justifyContent: 'center',
  [theme.breakpoints.down('md')]: {
    padding: 25,
  },
}))

const CountdownDesktopWrapper = styled('div', { name: 'CountdownDesktopWrapper' })(({ theme }) => ({
  display: 'flex',
  paddingRight: 30,
  [theme.breakpoints.down('md')]: {
    paddingRight: 25,
  },
}))

const MediaWrapper = styled('div', { name: 'MediaWrapper' })<{
  alignTextCenter: boolean | undefined
  fullWidth?: boolean
}>(({ alignTextCenter, fullWidth, theme }) => ({
  display: 'flex',
  justifyContent: alignTextCenter ? 'center' : 'flex-start',
  alignItems: 'center',

  '& img': {
    objectFit: 'contain',
    maxWidth: '150px',
    maxHeight: '80px',
    marginRight: 'auto',
    '@media (min-width: 895px)': {
      maxWidth: fullWidth ? '150px' : '90px',
    },
  },
  [theme.breakpoints.down('md')]: {
    '& img': {
      objectFit: 'contain',
      maxWidth: '90px',
    },
  },
}))

const PromoImageWrapper = styled('div', { name: 'PromoImageWrapper' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'stretch',
  justifyContent: 'center',
  maxWidth: '320px',
  paddingRight: 25,

  [theme.breakpoints.down('md')]: {
    maxWidth: '150px',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    alignSelf: 'center',
    objectFit: 'cover',
  },
}))

const HighlightCard: FC<PropTypes> = ({ highlight, brandLogoUrl, isExpoHomepage, assets }) => {
  const {
    title,
    description,
    cta,
    fullWidth,
    enableCountdown,
    countdownDate,
    whiteText,
    alignTextCenter,
    ui3styling,
    backgroundUrl,
    backgroundColor,
    show_brand_logo,
    brand_logo_url,
    enablePromoImage,
  } = highlight

  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))
  const enabledAlignTextToCenter = !enableCountdown && alignTextCenter
  if (!highlight) return null
  return (
    <CardWrapper
      style={{
        flexBasis: fullWidth || enableCountdown ? '100%' : undefined,
        color: whiteText ? 'white' : undefined,
      }}
      backgroundUrl={backgroundUrl || ''}
      backgroundColor={backgroundColor || ''}
      ui3styling={ui3styling}
    >
      <MainContent enabledAlignTextToCenter={enabledAlignTextToCenter}>
        {show_brand_logo && brandLogoUrl && (
          <MediaWrapper alignTextCenter={alignTextCenter} fullWidth={fullWidth}>
            <img src={brandLogoUrl} />
          </MediaWrapper>
        )}
        {brand_logo_url && (
          <MediaWrapper alignTextCenter={alignTextCenter} fullWidth={fullWidth}>
            <img src={brand_logo_url} />
          </MediaWrapper>
        )}
        {title && (
          <CardTitle ui3styling={ui3styling} fullWidth={fullWidth}>
            {title}
          </CardTitle>
        )}
        {description && <CardDesc ui3styling={ui3styling}>{description}</CardDesc>}
        {isMobile && enableCountdown && countdownDate && (
          <Countdown endDateTime={countdownDate} ui3styling={ui3styling} />
        )}
        {cta && cta.label && (
          <>
            {cta.type === 'url' ? (
              <Link href={cta.url} target="_blank">
                <Button
                  variant="contained"
                  color={isExpoHomepage ? 'primary' : 'secondary'}
                  size="small"
                  onClick={cta.onClick}
                >
                  {cta.label}
                </Button>
              </Link>
            ) : (
              <Link href={cta.file} download={'expo_highlight'}>
                <Button
                  variant="contained"
                  color={isExpoHomepage ? 'primary' : 'secondary'}
                  size="small"
                  onClick={cta.onClick}
                >
                  {cta.label}
                </Button>
              </Link>
            )}
          </>
        )}
      </MainContent>

      {!isMobile && enableCountdown && countdownDate && (
        <CountdownDesktopWrapper>
          <Countdown endDateTime={countdownDate} ui3styling={ui3styling} />
        </CountdownDesktopWrapper>
      )}

      {enablePromoImage && assets?.promoImage && (
        <PromoImageWrapper>
          <img src={assets.promoImage} alt="Promo" />
        </PromoImageWrapper>
      )}
    </CardWrapper>
  )
}

export default HighlightCard
